import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { UrlService } from '../../../../../environments/urlService';
import { InvestorCapitalAccountDTO } from '../../models/DTOs/capital-account/InvestorCapitalAccountDTO';
import { Observable } from 'rxjs';
import { IS_SPINNER_ACTIVE } from '../../../../util/interceptors';
import { ItemAllocationMethodDTO } from '../../models/DTOs/capital-account/ItemAllocationMethodDTO';
import { InvestorCapitalAccountItemValueDTO } from '../../models/DTOs/capital-account/InvestorCapitalAccountItemValueDTO';
import { CustomAllocationDTO } from '../../models/DTOs/capital-account/CustomAllocationDTO';
import { CustomAllocationValueDTO } from '../../models/DTOs/capital-account/CustomAllocationValueDTO';
import { InvestorCapitalAccountCarriedInterestDTO } from '../../models/DTOs/capital-account/InvestorCapitalAccountCarriedInterestDTO';
import { CarriedInterestSelectorDataDTO } from '../../models/DTOs/capital-account/CarriedInterestSelectorDataDTO';

@Injectable({
  providedIn: 'root',
})
export class CapitalAccountBackendService {

  private readonly capitalAccountUrl: string;
  private readonly carriedInterestUrl: string;
  private readonly customAllocationsUrl: string;

  constructor(
    private httpClient: HttpClient,
    private urlService: UrlService,
  ) {
    this.capitalAccountUrl = this.urlService.getFmsUrl() + 'financial-statement/capital-account';
    this.carriedInterestUrl = this.capitalAccountUrl + '/carried-interest';
    this.customAllocationsUrl = this.capitalAccountUrl + '/custom-allocations';
  }


  saveCapitalAccountInvestors(investors: InvestorCapitalAccountDTO[], fsUUID: string): Observable<void> {
    return this.httpClient.post<void>(this.capitalAccountUrl + '/investors', investors, {
      params: { fsUUID },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  deleteCapitalAccountInvestors(investors: InvestorCapitalAccountDTO[]): Observable<void> {
    return this.httpClient.delete<void>(this.capitalAccountUrl + '/investors', {
      body: investors,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  getCapitalAccountInvestors(fsUUID: string): Observable<InvestorCapitalAccountDTO[]> {
    return this.httpClient.get<InvestorCapitalAccountDTO[]>(this.capitalAccountUrl + '/investors', {
      params: { fsUUID },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  getAllocationMethodsForItems(fsUUID: string): Observable<ItemAllocationMethodDTO[]> {
    return this.httpClient.get<ItemAllocationMethodDTO[]>(this.capitalAccountUrl + '/allocation-methods', {
      params: { fsUUID },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  setItemAllocationMethods(itemAllocationMethodDTOs: ItemAllocationMethodDTO[], fsUUID: string): Observable<void> {
    return this.httpClient.put<void>(this.capitalAccountUrl + '/allocation-methods', itemAllocationMethodDTOs, {
      params: { fsUUID },
      withCredentials: true,
    });
  }

  getInvestorCapitalAccountItemValues(fsUUID: string): Observable<InvestorCapitalAccountItemValueDTO[]> {
    return this.httpClient.get<InvestorCapitalAccountItemValueDTO[]>(this.capitalAccountUrl + '/manual-values', {
      params: { fsUUID },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  setInvestorCapitalAccountItemValues(dtos: InvestorCapitalAccountItemValueDTO[]): Observable<void> {
    return this.httpClient.put<void>(this.capitalAccountUrl + '/manual-values', dtos, {
      withCredentials: true,
    });
  }

  getCustomAllocations(fsUUID: string): Observable<CustomAllocationDTO[]> {
    return this.httpClient.get<CustomAllocationDTO[]>(this.customAllocationsUrl, {
      params: { fsUUID },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  setCustomAllocations(customAllocations: CustomAllocationDTO[], fsUUID: string): Observable<void> {
    return this.httpClient.put<void>(this.customAllocationsUrl, customAllocations, {
      params: { fsUUID },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  deleteCustomAllocation(customAllocation: CustomAllocationDTO): Observable<void> {
    return this.httpClient.delete<void>(this.capitalAccountUrl + '/custom-allocation', {
      body: customAllocation,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  getCustomAllocationValues(fsUUID: string): Observable<CustomAllocationValueDTO[]> {
    return this.httpClient.get<CustomAllocationValueDTO[]>(this.customAllocationsUrl + '/values', {
      params: { fsUUID },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  setCustomAllocationValues(customAllocationValues: CustomAllocationValueDTO[]): Observable<void> {
    return this.httpClient.put<void>(this.customAllocationsUrl + '/values', customAllocationValues, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  updateInvestor(investorCapitalAccountDTO: InvestorCapitalAccountDTO): Observable<void> {
    return this.httpClient.put<void>(this.capitalAccountUrl + '/investor', investorCapitalAccountDTO, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  getCarriedInterestValues(fsUUID: string): Observable<InvestorCapitalAccountCarriedInterestDTO[]> {
    return this.httpClient.get<InvestorCapitalAccountCarriedInterestDTO[]>(this.carriedInterestUrl + '/values', {
      params: { fsUUID },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  setCarriedInterestValues(investorCapitalAccountCarriedInterestDTOS: InvestorCapitalAccountCarriedInterestDTO[]): Observable<void> {
    return this.httpClient.put<void>(this.carriedInterestUrl + '/values', investorCapitalAccountCarriedInterestDTOS, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  getCarriedInterestSelectorData(fsUUID: string): Observable<CarriedInterestSelectorDataDTO> {
    return this.httpClient.get<CarriedInterestSelectorDataDTO>(this.carriedInterestUrl + '/selector', {
      params: { fsUUID },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  setCarriedInterestSelectorData(carriedInterestSelectorDataDTO: CarriedInterestSelectorDataDTO, fsUUID: string): Observable<void> {
    return this.httpClient.put<void>(this.carriedInterestUrl + '/selector', carriedInterestSelectorDataDTO, {
      params: { fsUUID },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }
}
